import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Modal, Button, Spin } from 'antd';
import {
  MonitorPlay,
  RotateCcw,
  Clock,
  Film,
  AlertCircle,
  Video,
} from 'lucide-react';
import { Scene } from '../../../../types/scene';
import SceneService from '../../services/sceneService';
import logger from '../../utils/logger';
import SceneAnalysis from '../scenes/SceneAnalysis';
import { CompositorPlayer } from '../../compositor/components/CompositorPlayer';
import { CompositorRecordingData } from '../../compositor/types/manifest';
import './ScenePreviewModal.scss';

// Helper to check if scene metadata indicates separate stream recording
function hasSeparateStreamRecordingMetadata(sceneMetadata: any): boolean {
  if (!sceneMetadata) return false;
  try {
    const metadata = typeof sceneMetadata === 'string' 
      ? JSON.parse(sceneMetadata) 
      : sceneMetadata;
    if (metadata.recordingType !== 'separate_streams') return false;
    const streams = metadata.streams;
    if (!streams) return false;
    const hasWebcamWithKeys = streams.webcam?.segments?.some((s: any) => s.key);
    const hasScreenWithKeys = streams.screen?.segments?.some((s: any) => s.key);
    const hasAudioWithKeys = streams.audio?.segments?.some((s: any) => s.key || s.hasMic);
    return hasWebcamWithKeys || hasScreenWithKeys || hasAudioWithKeys;
  } catch {
    return false;
  }
}

// Helper to check if scene has audio segments for separate stream recordings
// Returns undefined for legacy recordings (let component use its own logic)
function checkSceneHasAudioSegments(sceneMetadata: any): boolean | undefined {
  if (!sceneMetadata) return undefined;
  try {
    const metadata = typeof sceneMetadata === 'string' 
      ? JSON.parse(sceneMetadata) 
      : sceneMetadata;
    // Only apply this check for separate stream recordings
    if (metadata.recordingType !== 'separate_streams') return undefined;
    
    const streams = metadata.streams;
    if (!streams?.audio?.segments) return false;
    
    // Check if any audio segments have keys (were actually recorded)
    const hasAudioWithKeys = streams.audio.segments.some((s: any) => s.key);
    return hasAudioWithKeys;
  } catch {
    return undefined;
  }
}

// Build CompositorRecordingData from saved scene metadata
function buildRecordingDataFromSavedScene(scene: Scene): CompositorRecordingData | null {
  try {
    const sceneWithMeta = scene as any;
    if (!sceneWithMeta.metadata) return null;
    
    const metadata = typeof sceneWithMeta.metadata === 'string' 
      ? JSON.parse(sceneWithMeta.metadata) 
      : sceneWithMeta.metadata;
    
    if (metadata.recordingType !== 'separate_streams') return null;

    // Build background config - layout.background is the CORRECT source
    // layout.background is what was actually on canvas when recording started (user's actual selection)
    // metadata.background is just the template default (e.g., pitch-deck-bg.jpeg) and should NOT override
    // This matches the FFmpeg jobParser.js priority order
    const background = metadata.layout?.background || { type: 'color', value: '#1a1a2e' };
    console.log('[ScenePreviewModal] Using layout.background (actual recorded canvas):', background);
    
    // Build the recording data from metadata
    const recordingData: CompositorRecordingData = {
      duration: scene.duration || metadata.duration || 0,
      layout: {
        canvas: metadata.layout?.canvas || { width: 1920, height: 1080, aspectRatio: '16:9' },
        webcam: metadata.layout?.webcam,
        screen: metadata.layout?.screen,
        background,
      },
      streams: {
        webcam: metadata.streams?.webcam,
        screen: metadata.streams?.screen,
        audio: metadata.streams?.audio,
      },
      version: metadata.version,
    };

    return recordingData;
  } catch (err) {
    logger.error('[ScenePreviewModal] Failed to build recording data from scene:', err);
    return null;
  }
}

interface ScenePreviewModalProps {
  visible: boolean;
  scene: Scene | null;
  projectId?: string;
  onClose: () => void;
  onRetake?: (sceneId: string) => void;
}

export const ScenePreviewModal: React.FC<ScenePreviewModalProps> = ({
  visible,
  scene,
  projectId,
  onClose,
  onRetake,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [freshScene, setFreshScene] = useState<Scene | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if this is a separate stream recording
  const isSeparateStreamRecording = useMemo(() => {
    const sceneToCheck = freshScene || scene;
    if (!sceneToCheck) return false;
    return hasSeparateStreamRecordingMetadata((sceneToCheck as any).metadata);
  }, [freshScene, scene]);

  // Build compositor recording data for separate stream recordings
  const compositorRecordingData = useMemo(() => {
    if (!isSeparateStreamRecording) return null;
    const sceneToUse = freshScene || scene;
    if (!sceneToUse) return null;
    return buildRecordingDataFromSavedScene(sceneToUse);
  }, [isSeparateStreamRecording, freshScene, scene]);

  useEffect(() => {
    if (!visible || !scene) {
      setFreshScene(null);
      return;
    }

    const loadFreshSceneData = async () => {
      setLoading(true);
      setError(null);
      try {
        const sceneData = await SceneService.getSceneById(scene.id, projectId);
        
        if (!sceneData) {
          setError('Scene not found');
          return;
        }

        // Check if this is a separate stream recording BEFORE checking videoUrl
        const isSeparateStreams = hasSeparateStreamRecordingMetadata((sceneData as any).metadata);

        // Only require videoUrl for traditional recordings
        if (!sceneData.videoUrl && !isSeparateStreams) {
          setError('Video is still uploading. Please wait a moment and try again.');
          return;
        }

        setFreshScene(sceneData);
      } catch (err: any) {
        setError(`Failed to load video: ${err?.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    loadFreshSceneData();
  }, [visible, scene?.id, scene?.videoGuid, projectId]);

  const prevVideoUrlRef = useRef<string | null>(null);
  const currentVideoUrl = freshScene?.videoUrl || null;

  useEffect(() => {
    const hasVideoUrlChanged = currentVideoUrl !== prevVideoUrlRef.current;
    
    if (visible && videoRef.current && currentVideoUrl) {
      if (hasVideoUrlChanged) {
        prevVideoUrlRef.current = currentVideoUrl;
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
    
    return () => {
      if (!visible) {
        const video = videoRef.current;
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
        setIsPlaying(false);
        setCurrentTime(0);
        prevVideoUrlRef.current = null;
      }
    };
  }, [visible, currentVideoUrl]);

  useEffect(() => {
    if (!visible) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      switch (e.key) {
        case ' ':
          e.preventDefault();
          if (videoRef.current) {
            if (isPlaying) {
              videoRef.current.pause();
            } else {
              videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
          }
          break;
        case 'r':
          e.preventDefault();
          if (videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.play();
            setCurrentTime(0);
            setIsPlaying(true);
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (videoRef.current) {
            const newTime = Math.max(0, videoRef.current.currentTime - 5);
            videoRef.current.currentTime = newTime;
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (videoRef.current) {
            const newTime = Math.min(duration, videoRef.current.currentTime + 5);
            videoRef.current.currentTime = newTime;
          }
          break;
        case 'Escape':
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [visible, onClose, duration, isPlaying]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const displayScene = freshScene || scene;
  const videoUrl = freshScene?.videoUrl || scene?.videoUrl;

  const modalTitle = (
    <div className="modal-header-content">
      <div className="header-icon-badge">
        <MonitorPlay size={20} />
      </div>
      <div className="header-text">
        <h3 className="header-title">{displayScene?.title || 'Scene Preview'}</h3>
        {displayScene && (
          <p className="header-subtitle">
            Review your recorded scene
          </p>
        )}
      </div>
    </div>
  );

  const footerContent = (
    <>
      <div className="footer-left">
        <Button
          icon={<RotateCcw size={14} />}
          onClick={() => {
            if (scene) {
              onRetake?.(String(scene.id));
              onClose();
            }
          }}
        >
          Retake
        </Button>
      </div>
      <div className="footer-right">
        <Button type="primary" onClick={onClose}>
          Close
        </Button>
      </div>
    </>
  );

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onClose}
      footer={footerContent}
      width={900}
      className="scene-preview-modal preview-modal-redesigned"
      centered
      destroyOnHidden
    >
      <div className="scene-preview-content">
        {loading ? (
          <div className="preview-loading-state">
            <Spin size="large" />
            <p className="loading-text">Loading video from cloud...</p>
          </div>
        ) : error ? (
          <div className="preview-error-state">
            <div className="error-icon">
              <AlertCircle />
            </div>
            <h4 className="error-title">Unable to Load Video</h4>
            <p className="error-description">{error}</p>
            <Button onClick={onClose} style={{ marginTop: 16 }}>Close</Button>
          </div>
        ) : isSeparateStreamRecording && compositorRecordingData ? (
          <>
            <div className="preview-video-section">
              <div className="video-wrapper compositor-wrapper">
                {displayScene && (
                  <div className="scene-badge">
                    <Film size={12} />
                    <span>{displayScene.title}</span>
                  </div>
                )}
                
                <CompositorPlayer
                  recordingData={compositorRecordingData}
                  autoPlay={true}
                  showControls={true}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div className="keyboard-hints">
              <span className="hint-item">
                <kbd>Esc</kbd> Close
              </span>
            </div>
            
            {freshScene && (
              <div className="scene-analysis-wrapper">
                <SceneAnalysis 
                  sceneId={parseInt(freshScene.id)} 
                  micEnabled={freshScene.micEnabled ?? false}
                  scene={freshScene as any}
                  projectId={projectId ? parseInt(projectId) : undefined}
                  hasAudioSegments={checkSceneHasAudioSegments((freshScene as any).metadata)}
                />
              </div>
            )}
          </>
        ) : videoUrl ? (
          <>
            <div className="preview-video-section">
              <div className="video-wrapper">
                {displayScene && (
                  <div className="scene-badge">
                    <Film size={12} />
                    <span>{displayScene.title}</span>
                  </div>
                )}
                
                <video
                  ref={videoRef}
                  src={videoUrl}
                  controls
                  autoPlay
                  onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                  onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />
                
                {duration > 0 && (
                  <div className="duration-badge">
                    <Clock size={12} />
                    <span>{formatTime(duration)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="keyboard-hints">
              <span className="hint-item">
                <kbd>Space</kbd> Play/Pause
              </span>
              <span className="hint-item">
                <kbd>R</kbd> Restart
              </span>
              <span className="hint-item">
                <kbd>←</kbd><kbd>→</kbd> Seek 5s
              </span>
              <span className="hint-item">
                <kbd>Esc</kbd> Close
              </span>
            </div>
            
            {freshScene && (
              <div className="scene-analysis-wrapper">
                <SceneAnalysis 
                  sceneId={parseInt(freshScene.id)} 
                  micEnabled={freshScene.micEnabled ?? false}
                  scene={freshScene as any}
                  projectId={projectId ? parseInt(projectId) : undefined}
                  hasAudioSegments={checkSceneHasAudioSegments((freshScene as any).metadata)}
                />
              </div>
            )}
          </>
        ) : (
          <div className="preview-empty-state">
            <div className="empty-icon">
              <Video />
            </div>
            <h4 className="empty-title">No Video Available</h4>
            <p className="empty-description">This scene doesn't have a recording yet.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};
